/**
 * Sekmeler — **el** mertebesi. Vuexy 10.11 `nav nav-pills` BİREBİR ölçü
 * (pages-account-settings-account.html + core.css):
 *
 *   .nav        gap yok, satır; .nav-pills .nav-link radius .375rem (6px)
 *   .nav-link   padding .5435rem 1.25rem (≈9/20px), 15px / 500, renk: başlık rengi
 *   .active     zemin primary, metin beyaz, gölge 0 .125rem .375rem rgba(primary,.3)
 *   hover       (pasif) metin primary
 *   şerit       YOK — sekmeler zeminde doğrudan durur; altında `mb-6` (24px) kart gelir
 *
 * Önceki iki sürüm ölçülmüş kusurlardı: alt çizgili düğmeler `overflow-x:auto`
 * kapta dikey kaydırma çubuğu çiziyordu; sonra "sunken şerit" içine alınan haplar
 * bir araç çubuğu gibi okunuyor, sekme olduğu yine anlaşılmıyordu.
 *
 * Klavye: ← → Home End (WAI-ARIA tabs); yalnızca seçili sekme Tab sırasındadır.
 */

import type { KeyboardEvent } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface SekmeTanimi<K extends string> {
  key: K;
  etiket: string;
  ikon?: LucideIcon;
}

interface SekmelerProps<K extends string> {
  sekmeler: readonly SekmeTanimi<K>[];
  secili: K;
  onSec: (key: K) => void;
  /** Ekran okuyucu için grubun adı ("Ayarlar bölümleri"). */
  etiket: string;
}

export function Sekmeler<K extends string>({ sekmeler, secili, onSec, etiket }: SekmelerProps<K>) {
  const klavye = (olay: KeyboardEvent<HTMLDivElement>) => {
    const simdiki = sekmeler.findIndex((s) => s.key === secili);
    if (simdiki === -1) return;
    const son = sekmeler.length - 1;
    let hedef = -1;
    if (olay.key === 'ArrowRight') hedef = simdiki === son ? 0 : simdiki + 1;
    else if (olay.key === 'ArrowLeft') hedef = simdiki === 0 ? son : simdiki - 1;
    else if (olay.key === 'Home') hedef = 0;
    else if (olay.key === 'End') hedef = son;
    if (hedef === -1) return;

    olay.preventDefault();
    const yeni = sekmeler[hedef]!.key;
    onSec(yeni);
    olay.currentTarget.querySelector<HTMLButtonElement>(`[data-sekme="${yeni}"]`)?.focus();
  };

  return (
    // Vuexy: `nav nav-pills flex-column flex-md-row gap-2 gap-md-0` — darda alt alta.
    <div
      role="tablist"
      aria-label={etiket}
      onKeyDown={klavye}
      className="flex flex-col gap-2 @xl:flex-row @xl:gap-0"
    >
      {sekmeler.map(({ key, etiket: ad, ikon: Ikon }) => {
        const aktif = key === secili;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            data-sekme={key}
            aria-selected={aktif}
            tabIndex={aktif ? 0 : -1}
            onClick={() => onSec(key)}
            className={cn(
              // .nav-link: 9px/20px dolgu, 15px/500 (ölçekte 16 yok → 15 Vuexy değeri korunur), radius 6
              'inline-flex items-center gap-1.5 rounded-[var(--radius-sm)] px-5 py-[0.5435rem] text-[0.9375rem] leading-[1.375] font-medium whitespace-nowrap',
              'transition-[color,background-color,box-shadow] duration-150',
              'focus-visible:shadow-[var(--focus-ring)] focus-visible:outline-none',
              aktif
                ? 'bg-[var(--accent)] text-[var(--accent-ink)] shadow-[var(--shadow-vurgu)]'
                : 'text-[var(--ink)] hover:text-[var(--accent)]'
            )}
          >
            {Ikon && (
              // .icon-sm me-1_5 — ikon 1px yukarı (optik düzeltme, docs/13)
              <span className="-mt-px" aria-hidden="true">
                <Ikon size={18} strokeWidth={1.75} />
              </span>
            )}
            {ad}
          </button>
        );
      })}
    </div>
  );
}
